/**
 * useDocumentSqlButton — przycisk SQL (klocek układu `sql_button`) na stronie dokumentu, wspólny dla
 * 7 klas dokumentów (faktury zakupu i sprzedaży, umowy, pisma, dokumenty handlowe, wewnętrzne i specjalne).
 *
 * Składa w jednym miejscu to, co strona kontrahenta ma rozpisane w swoim pliku — strony dokumentów różnią
 * się tylko klasą (od niej zależą klucze zapytań rekordu i listy), reszta jest taka sama:
 *   - stan zadań rekordu (`useRecordProcedureRuns('document', …, { recordClass })`): odczyt przy wejściu
 *     na kartę, odpytywanie co ~2 s tylko przy aktywnym zadaniu, po jego końcu komunikat w rogu i
 *     odświeżenie dokumentu (zapytanie mogło go zmienić);
 *   - blokada rekordu na czas zadania (decyzja „A”): pasek `lockBanner` nad formularzem i `recordLocked`
 *     do wyłączenia „Edytuj” / „Usuń” (serwer odrzuca zapis i usunięcie tak czy inaczej — 409);
 *   - renderer klocka `sql_button` do `contextRenderers`: w edycji klik najpierw wykonuje ZWYKŁY zapis
 *     formularza (ta sama bramka co przyciski decyzji — `useSaveBeforeDecision`; błąd zapisu = zadanie
 *     nie rusza), w podglądzie rusza od razu, a na NOWYM dokumencie: utworzenie → zadanie → przejście
 *     pod adres dokumentu (`runPendingCreateRun` woła strona w `onSuccess` tworzenia, przed nawigacją).
 *
 * Strona woła hak PO `useSaveBeforeDecision` (potrzebuje jego wyniku) i PRZED mutacją zapisu — jej
 * `onSuccess` / `onError` używają `runPendingCreateRun`, `onSaveAborted` i `handleLockedError`. Wartości,
 * które powstają dopiero po deklaracji mutacji (`canEdit` przycisku, `saveMutation.isPending`), przekazuje
 * się do `renderSqlButton` w miejscu składania `contextRenderers`.
 */

import { type ReactElement, type ReactNode, useMemo, useRef } from 'react';
import type { DocumentClass } from '@saas/shared';
import { useQueryClient } from '@tanstack/react-query';

import type { LayoutElement } from '@/components/form/RecursiveFormRenderer';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import {
  useRecordProcedureRuns,
  type UseRecordProcedureRunsResult,
} from '@/hooks/useRecordProcedureRuns';
import type { UseSaveBeforeDecisionResult } from '@/hooks/useSaveBeforeDecision';

/**
 * Klucze zapytań strony dokumentu danej klasy: rekord (`[detailKey, id]`), lista (`[listKey]`) i slug
 * modułu systemowego (zakładka „Historia zmian" czyta `['system-module-audit', moduleSlug, id, …]`).
 * Muszą zgadzać się z kluczami używanymi przez strony `*Detail.tsx` i listy.
 */
export const DOCUMENT_RUN_QUERY_KEYS: Readonly<
  Record<DocumentClass, { moduleSlug: string; detailKey: string; listKey: string }>
> = {
  purchase_invoice: {
    moduleSlug: 'purchase_invoices',
    detailKey: 'purchase-invoice',
    listKey: 'purchase-invoices',
  },
  sale_invoice: {
    moduleSlug: 'sale_invoices',
    detailKey: 'sale-invoice',
    listKey: 'sale-invoices',
  },
  contract: { moduleSlug: 'contracts', detailKey: 'contract', listKey: 'contracts' },
  letter: { moduleSlug: 'letters', detailKey: 'letter', listKey: 'letters' },
  commercial: {
    moduleSlug: 'commercial',
    detailKey: 'commercial-doc',
    listKey: 'commercial-docs',
  },
  internal: { moduleSlug: 'internal', detailKey: 'internal-doc', listKey: 'internal-docs' },
  specjalne: { moduleSlug: 'specjalne', detailKey: 'special-doc', listKey: 'special-docs' },
};

export interface UseDocumentSqlButtonOptions {
  /** Klasa dokumentu strony — wysyłana do serwera razem z typem `document`. */
  recordClass: DocumentClass;
  /** `true` = strona nowego dokumentu (rekord jeszcze nie istnieje). */
  isCreate: boolean;
  /** Id zapisanego dokumentu z adresu (przy tworzeniu pomijane). */
  recordId: string | undefined;
  /** Układ formularza, z którego strona renderuje klocki — stąd napisy przycisków do komunikatów. */
  layoutConfig: unknown;
  /** Zwykły zapis formularza (to samo co „Zapisz”); `false` = zatrzymała go walidacja w panelu. */
  save: () => boolean;
  /** Firma docelowa nowego dokumentu — nagłówek firmy zlecenia (w trybie „Wszystkie firmy” brak aktywnej). */
  createCompanyId: string | null | undefined;
  /** Wynik `useSaveBeforeDecision` strony — bramka zapisu, klucz mutacji zapisu i `aborted`. */
  saveBeforeDecision: UseSaveBeforeDecisionResult;
}

/** Wartości znane dopiero po deklaracji mutacji zapisu — podawane w miejscu składania `contextRenderers`. */
export interface DocumentSqlButtonRenderDeps {
  /** Prawo edycji dokumentu (ten sam sygnał co „Edytuj”); bez niego przycisk jest wyszarzony. */
  canEdit: boolean;
  /** Zapis dokumentu trwa (`saveMutation.isPending`) — przycisk nieaktywny, drugi zapis nie wystartuje. */
  savePending: boolean;
}

export interface UseDocumentSqlButtonResult {
  runs: UseRecordProcedureRunsResult;
  /** Zadanie przycisku w kolejce / w toku albo właśnie zlecane — „Edytuj” i „Usuń” nieaktywne. */
  recordLocked: boolean;
  /** Pasek „Trwa zadanie…” nad formularzem (null, gdy nic nie trwa). */
  lockBanner: ReactElement | null;
  /**
   * Błąd zapisu / usunięcia / przywrócenia dokumentu: blokada na czas zadania → czytelny komunikat + pasek
   * i `true`; inaczej `false` (strona pokazuje swój zwykły komunikat błędu).
   */
  handleLockedError: (err: unknown) => boolean;
  /**
   * Zapis się nie udał albo anulowano ostrzeżenie kontroli: zapomina okno decyzji (`aborted` bramki zapisu)
   * i kończy oczekiwanie przycisku klikniętego na nowym dokumencie — zadanie nie rusza.
   */
  onSaveAborted: () => void;
  /**
   * Zleca zadanie zapamiętane przy kliknięciu na NOWYM dokumencie — z id świeżo utworzonego dokumentu.
   * Strona woła to w `onSuccess` tworzenia, PRZED przejściem pod adres dokumentu. No-op bez zapamiętanego zadania.
   */
  runPendingCreateRun: (newRecordId: string) => Promise<void>;
  /** Renderer klocka `sql_button` do mapy `contextRenderers` strony. */
  renderSqlButton: (
    deps: DocumentSqlButtonRenderDeps,
  ) => (props: { element: LayoutElement }) => ReactNode;
}

export function useDocumentSqlButton({
  recordClass,
  isCreate,
  recordId,
  layoutConfig,
  save,
  createCompanyId,
  saveBeforeDecision,
}: UseDocumentSqlButtonOptions): UseDocumentSqlButtonResult {
  const queryClient = useQueryClient();
  const keys = DOCUMENT_RUN_QUERY_KEYS[recordClass];

  // Napisy przycisków z układu — zadanie zna tylko nazwę procedury, a użytkownik zna przycisk po napisie.
  const labels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const documentId = isCreate ? undefined : recordId;
  const runs = useRecordProcedureRuns('document', documentId, {
    recordClass,
    labels,
    onFinished: () => {
      void queryClient.invalidateQueries({ queryKey: [keys.detailKey, documentId] });
      void queryClient.invalidateQueries({ queryKey: [keys.listKey] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({ queryKey: ['document-parties', documentId] });
      void queryClient.invalidateQueries({
        queryKey: ['system-module-audit', keys.moduleSlug, documentId],
      });
    },
  });

  // Przycisk kliknięty na NOWYM dokumencie: najpierw utworzenie (zwykły zapis), potem zadanie z id świeżo
  // utworzonego dokumentu, dopiero potem przejście pod jego adres (`runPendingCreateRun` w `onSuccess`
  // tworzenia). `settle` kończy oczekiwanie przycisku (kręciołek), także przy porażce zapisu.
  const pendingCreateRunRef = useRef<{ procedureId: string; settle: () => void } | null>(null);

  function settlePendingCreateRun(): void {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    pending?.settle();
  }

  async function runPendingCreateRun(newRecordId: string): Promise<void> {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    if (pending === null) return;
    try {
      // Zlecenie niesie tę samą firmę docelową, którą POST tworzący wysłał jawnie (nagłówek firmy).
      await runs.startRun(pending.procedureId, newRecordId, createCompanyId || undefined);
    } finally {
      pending.settle();
    }
  }

  /**
   * Start zadania na NOWYM dokumencie: zapamiętuje zadanie i uruchamia zwykły zapis (utworzenie) — resztę robi
   * `onSuccess` tworzenia strony. W edycji i podglądzie przycisk woła `runs.startRun` wprost, po bramce zapisu.
   */
  function startOnNewRecord(procedureId: string): Promise<unknown> {
    // Utworzenie już trwa (kliknięte „Utwórz” albo wcześniejszy przycisk) — drugi POST utworzyłby dokument
    // drugi raz. Twardy bezpiecznik: stan mutacji czytany na żywo (`useIsMutating` odświeża się dopiero po
    // renderze, a kliknięcie może wyprzedzić render).
    if (
      queryClient.isMutating({ mutationKey: saveBeforeDecision.saveMutationKey, exact: true }) > 0
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      pendingCreateRunRef.current = { procedureId, settle: resolve };
      try {
        if (!save()) settlePendingCreateRun();
      } catch (err) {
        // Wyjątek zanim zapis ruszył: zapamiętane zadanie nie może zostać w refie (wystartowałoby po
        // następnym, zwykłym zapisie) — czyścimy je i odrzucamy obietnicę (przycisk wraca do gotowości).
        pendingCreateRunRef.current = null;
        reject(err);
      }
    });
  }

  function onSaveAborted(): void {
    saveBeforeDecision.aborted();
    settlePendingCreateRun();
  }

  const recordLocked = runs.active !== null || runs.starting;

  const lockBanner: ReactElement | null = runs.active ? (
    <RecordRunLockBanner
      run={runs.active}
      label={runs.labelFor(runs.active)}
      refreshFailed={runs.isError}
    />
  ) : null;

  function renderSqlButton({
    canEdit,
    savePending,
  }: DocumentSqlButtonRenderDeps): (props: { element: LayoutElement }) => ReactNode {
    return ({ element }) => {
      const cfg = parseSqlButtonSettings(element.settings);
      // Klocek bez procedury / napisu: nic (jak „Widok SQL” bez widoku).
      if (cfg === null) return null;
      return (
        <SqlButton
          label={cfg.label}
          procedureId={cfg.procedureId}
          active={runs.active}
          lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
          canEdit={canEdit}
          starting={runs.starting || savePending}
          beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
          onStart={(procedureId) =>
            isCreate ? startOnNewRecord(procedureId) : runs.startRun(procedureId)
          }
          testIdPrefix={`sql-button-${element.id}`}
        />
      );
    };
  }

  return {
    runs,
    recordLocked,
    lockBanner,
    handleLockedError: runs.handleLockedError,
    onSaveAborted,
    runPendingCreateRun,
    renderSqlButton,
  };
}
